import { css } from '@emotion/react';
import {
  OptimizeImage,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
  desktopFirstQuery,
} from '@inflearn/ds-react-next';
import { useMediaQuery } from '@mantine/hooks';
import { Trans, useTranslation } from 'next-i18next';
import Link from 'next/link';

import { NEXT_PUBLIC_CDN_INTEGRATED_HOST, NEXT_PUBLIC_CDN_URL } from '../../../../../../appConfig';
import { AUTH_URL, COURSE_URL, INFLEARN_URL } from '../../../../../constants/urls';
import { useReferUrlNavigation } from '../../../../../hooks/useReferUrlNavigation';
import { type EventNames, eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../utils/GTM/useTrackMixpanelEvent';
import { Confetti } from '../../../../common/Confetti';
import { MobileOnlyBreak } from '../../../../common/MobileOnlyBreak';
import { PARTNERS_TYPE } from '../form/constants';
import type { PartnersWithCommissionRate } from '../form/types';
import { PartnersUnstyledButton } from './PartnersUnstyledButton';

interface PartnersSignupCompleteProps {
  partners: PartnersWithCommissionRate;
}

const _SIGNUP_COMPLETE_MESSAGE = {
  [PARTNERS_TYPE.PERSONAL]: (
    <>
      지금부터 공유한 홍보 링크로 판매가 이루어지면, <MobileOnlyBreak />
      수익을 받을 수 있어요.
    </>
  ),

  [PARTNERS_TYPE.BUSINESS]: (
    <>
      지금부터 공유한 홍보 링크로 판매가 이루어지면, <MobileOnlyBreak />
      수익을 받을 수 있어요.
    </>
  ),
};

export const PartnersSignupComplete = ({ partners }: PartnersSignupCompleteProps) => {
  const { t } = useTranslation();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const handleTrack = (eventName: EventNames, eventProps: Record<string, string> = {}) => {
    trackMixpanelEvent(eventName, {
      marketing_partner_type: partners.type.toLowerCase() as 'business' | 'personal',
      name: partners.name,
      email: partners.email,
      country_code: `+${partners.countryCode}`,
      phone_number: partners.phone,
      partners_id: partners.id,
      commission_rate: partners.commissionRate,
      ...eventProps,
    });
  };

  const isSmallerThanMobile = useMediaQuery(desktopFirstQuery.mobile);

  const { getReferUrl } = useReferUrlNavigation();
  const referUrl = getReferUrl();
  const isChallenge = referUrl.startsWith(COURSE_URL.CHALLENGE_DETAIL(''));

  return (
    <>
      <Confetti count={50} />
      <Stack
        gap={48}
        styles={{
          root: styleWrapper,
        }}
      >
        <Stack gap={24} align="center">
          <OptimizeImage
            src={`${NEXT_PUBLIC_CDN_INTEGRATED_HOST}/image/apng/clapping-yellow-hands.png`}
            w={112 * 2}
            width={112}
            height={112}
            alt={t('파트너스 가입 완료')}
          />

          <Stack
            align="center"
            styles={{
              root: {
                gap: 12,

                [desktopFirstMediaQuery.mobile]: {
                  gap: 8,
                },
              },
            }}
          >
            <Text fz={isSmallerThanMobile ? 'sm' : 'md'} ta="center" fw={600} c="infgreen.7">
              {t('인프런 마케팅 파트너스 가입 완료')}
            </Text>
            <Title order={1} size={isSmallerThanMobile ? 'h4' : 'h3'} ta="center">
              {t('강의를 공유하면 누군가의 인생을 바꾸는')}
              <MobileOnlyBreak />
              {t('배움의 시작을 만들 수 있어요.')}
              <br />
              {t('그 가치를 함께 나눠보세요.')}
            </Title>
          </Stack>
          <Text fz={isSmallerThanMobile ? 'sm' : 'md'} ta="center">
            <Trans
              i18nKey="지금부터 공유한 홍보 링크로 판매가 이루어지면, <MobileOnlyBreak />수익을 받을 수 있어요."
              components={{
                MobileOnlyBreak: <MobileOnlyBreak />,
              }}
            />
          </Text>
        </Stack>
        <Stack gap={16}>
          {referUrl !== INFLEARN_URL.MAIN() && (
            <PartnersUnstyledButton
              imageSrc={`${NEXT_PUBLIC_CDN_URL}/assets/images/partners/running.png`}
              label={t("방금 본 {{isChallenge ? '챌린지' : '강의'}} 홍보하러 가기", {
                "isChallenge ? '챌린지' : '강의'": isChallenge ? '챌린지' : '강의',
              })}
              component="a"
              href={referUrl}
              target="_blank"
            />
          )}
          <PartnersUnstyledButton
            imageSrc={`${NEXT_PUBLIC_CDN_URL}/assets/images/partners/magnifying-glass-tilted-left.png`}
            label={t('홍보할 강의 둘러보기')}
            description={t('강의를 둘러보고 원하는 강의의 홍보 링크를 공유해 보세요.')}
            component="a"
            href={INFLEARN_URL.MAIN()}
            target="_blank"
            onClick={() =>
              handleTrack(eventNames.CLICK_COURSE_ON_PARTNERS_JOIN_COMPLETE, {
                button_link: INFLEARN_URL.MAIN(),
              })
            }
          />
          <PartnersUnstyledButton
            imageSrc={`${NEXT_PUBLIC_CDN_URL}/assets/images/partners/bookmark-tabs.png`}
            label={t('파트너스 가이드 확인하기')}
            description={t('마케팅 파트너스 활동을 위한 가이드를 확인해 보세요!')}
            component={Link}
            href={AUTH_URL.PARTNERS_GUIDE()}
            target="_blank"
            onClick={() =>
              handleTrack(eventNames.CLICK_GUIDE_ON_PARTNERS_JOIN_COMPLETE, {
                button_link: AUTH_URL.PARTNERS_GUIDE(),
              })
            }
          />
        </Stack>
      </Stack>
    </>
  );
};

const styleWrapper = css({
  [desktopFirstMediaQuery.mobile]: {
    gap: 40,
  },
});
